<svelte:options immutable={true} />

<script>
  import { onMount, createEventDispatcher } from "svelte";
  import Toolbar from "../Toolbar.svelte";
  import { pannable } from "../../actions/pannable.js";
  import { tapout } from "../../actions/tapout.js";
  import { sleep } from "../../lib/utils.js";
  import { Fonts, addCustomFont } from "../../lib/assets.js";

  export let fontSize;
  export let html;
  export let lineHeight;
  export let x;
  export let y;
  export let fontFamily;
  export let color = "#000000";
  export let opacity = 1;
  export let scaleX = 1;
  export let scaleY = 1;
  export let pageScale = 1;
  export let selected = false;
  export let showToolbar = true;
  export let hideBorders = false;

  const dispatch = createEventDispatcher();

  let wasEditing = false;
  $: {
    const isEditing = !!operation;
    if (isEditing !== wasEditing) {
      if (isEditing) dispatch("select");
      else dispatch("blur");
      wasEditing = isEditing;
    }
  }

  let startPointerX;
  let startPointerY;
  let editable;

  let draftFontSize = fontSize;
  let draftLineHeight = lineHeight;
  let draftFontFamily = fontFamily;
  let draftColor = color;
  let draftOpacity = opacity;
  let draftScaleX = scaleX;
  let draftScaleY = scaleY;

  let dx = 0;
  let dy = 0;
  let operation = "";

  function handlePanMove(event) {
    dx = (event.detail.x - startPointerX) / pageScale;
    dy = (event.detail.y - startPointerY) / pageScale;
  }

  function handlePanEnd() {
    if (dx === 0 && dy === 0) {
      editable.focus();
      return;
    }
    dispatch("update", {
      x: x + dx,
      y: y + dy,
    });
    dx = 0;
    dy = 0;
    operation = "";
  }

  function handlePanStart(event) {
    startPointerX = event.detail.x;
    startPointerY = event.detail.y;
    operation = "move";
  }

  function onFocus() {
    operation = "edit";
  }

  async function onBlur() {
    if (operation !== "edit") return;
    editable.blur();
    sanitize();
    dispatch("update", {
      html: editable.innerHTML,
      lines: extractLines(),
      width: editable.clientWidth,
      lineHeight: Number(draftLineHeight),
      fontSize: Number(draftFontSize),
      fontFamily: draftFontFamily,
      color: draftColor,
      opacity: Number(draftOpacity),
      scaleX: Number(draftScaleX),
      scaleY: Number(draftScaleY),
    });
    operation = "";
  }

  async function onPaste(e) {
    const pastedText = e.clipboardData.getData("text");
    document.execCommand("insertHTML", false, pastedText);
    await sleep();
    sanitize();
  }

  function onKeydown(e) {
    const childNodes = Array.from(editable.childNodes);
    if (e.keyCode === 13) {
      e.preventDefault();
      const selection = window.getSelection();
      const focusNode = selection.focusNode;
      const focusOffset = selection.focusOffset;

      if (focusNode === editable) {
        editable.insertBefore(
          document.createElement("br"),
          childNodes[focusOffset],
        );
      } else if (focusNode instanceof HTMLBRElement) {
        editable.insertBefore(document.createElement("br"), focusNode);
      } else if (focusNode.textContent.length !== focusOffset) {
        document.execCommand("insertHTML", false, "<br>");
      } else {
        let br = focusNode.nextSibling;
        if (br) {
          editable.insertBefore(document.createElement("br"), br);
        } else {
          br = editable.appendChild(document.createElement("br"));
          br = editable.appendChild(document.createElement("br"));
        }
        selection.collapse(br, 0);
      }
    }
  }

  function sanitize() {
    let weirdNode;
    while (
      (weirdNode = Array.from(editable.childNodes).find(
        (node) => !["#text", "BR"].includes(node.nodeName),
      ))
    ) {
      editable.removeChild(weirdNode);
    }
  }

  function onChangeFont() {
    if (draftFontFamily === "LOAD_FONT") {
      triggerFontUpload();
      return;
    }
    dispatch("fontFamilyChange", {
      fontFamily: draftFontFamily,
    });
  }

  function triggerFontUpload() {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".ttf";
    input.onchange = async (e) => {
      const file = e.target.files && e.target.files[0];
      if (!file) {
        draftFontFamily = fontFamily;
        return;
      }
      try {
        const newName = await addCustomFont(file);
        draftFontFamily = newName;
        onChangeFont();
      } catch (err) {
        draftFontFamily = fontFamily;
      }
    };
    input.click();
    draftFontFamily = fontFamily;
  }

  function render() {
    editable.innerHTML = html;
    editable.focus();
    dispatch("update", {
      lines: extractLines(),
      width: editable.clientWidth,
    });
  }

  function extractLines() {
    const nodes = editable.childNodes;
    const lines = [];
    let lineText = "";
    for (let index = 0; index < nodes.length; index++) {
      const node = nodes[index];
      if (node.nodeName === "BR") {
        lines.push(lineText);
        lineText = "";
      } else {
        lineText += node.textContent;
      }
    }
    lines.push(lineText);
    return lines;
  }

  function onDelete() {
    dispatch("delete");
  }

  onMount(render);
</script>

{#if operation && showToolbar}
  <Toolbar>
    <div class="flex items-center gap-3 px-1 my-1">
      <div class="flex items-center gap-2">
        <img src="/line_height.png" class="w-5 h-5" alt="" />
        <input
          type="number"
          min="1"
          max="10"
          step="0.1"
          class="h-8 w-16 text-center rounded-md border border-gray-200"
          bind:value={draftLineHeight}
        />
      </div>
      <div class="flex items-center gap-2">
        <img src="/text.png" class="w-5 h-5" alt="" />
        <input
          type="number"
          min="12"
          max="120"
          step="1"
          class="h-8 w-16 text-center rounded-md border border-gray-200"
          bind:value={draftFontSize}
        />
      </div>
      <div class="flex items-center gap-2">
        <img src="/text-family.png" class="w-4 h-4" alt="" />
        <div class="relative w-40">
          <select
            bind:value={draftFontFamily}
            on:change={onChangeFont}
            class="font-family"
          >
            {#each Object.keys(Fonts) as family}
              <option value={family}>{family}</option>
            {/each}
            <option value="LOAD_FONT">載入字體...</option>
          </select>
          <div
            class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700"
          >
            <svg
              class="fill-current h-4 w-4"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
            >
              <path
                d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"
              />
            </svg>
          </div>
        </div>
      </div>

      <div class="flex items-center gap-2 border-l border-gray-200 px-3">
        <span class="text-xs text-gray-500">W:</span>
        <input
          type="number"
          min="0.1"
          max="5"
          step="0.1"
          bind:value={draftScaleX}
          class="w-12 h-8 text-sm border-gray-300 rounded"
          title="Width ratio"
        />
        <span class="text-xs text-gray-500 ml-1">H:</span>
        <input
          type="number"
          min="0.1"
          max="5"
          step="0.1"
          bind:value={draftScaleY}
          class="w-12 h-8 text-sm border-gray-300 rounded"
          title="Height ratio"
        />
      </div>

      <div class="flex items-center gap-2 border-l border-gray-200 px-3">
        <span class="text-xs text-gray-500">Color:</span>
        <input
          type="color"
          bind:value={draftColor}
          on:input={() => dispatch("update", { color: draftColor })}
          class="w-8 h-8 rounded border-none cursor-pointer p-0"
        />
        <span class="text-xs text-gray-500 ml-1">透明度:</span>
        <input
          type="range"
          min="0"
          max="1"
          step="0.05"
          bind:value={draftOpacity}
          on:input={() => dispatch("update", { opacity: Number(draftOpacity) })}
          class="w-16 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          title="調整透明度"
        />
      </div>

      <div class="flex items-center gap-2 border-l border-gray-200 pl-3">
        <button
          type="button"
          class="w-5 h-5 rounded-full border border-gray-200 shadow-sm transition hover:scale-110"
          style="background-color: #000000;"
          aria-label="Black"
          on:click={() => {
            draftColor = "#000000";
            dispatch("update", { color: "#000000" });
          }}
        ></button>
        <button
          type="button"
          class="w-5 h-5 rounded-full border border-gray-200 shadow-sm transition hover:scale-110"
          style="background-color: #EF4444;"
          aria-label="Red"
          on:click={() => {
            draftColor = "#EF4444";
            dispatch("update", { color: "#EF4444" });
          }}
        ></button>
        <button
          type="button"
          class="w-5 h-5 rounded-full border border-gray-200 shadow-sm transition hover:scale-110"
          style="background-color: #3B82F6;"
          aria-label="Blue"
          on:click={() => {
            draftColor = "#3B82F6";
            dispatch("update", { color: "#3B82F6" });
          }}
        ></button>
        <button
          type="button"
          class="w-5 h-5 rounded-full border border-gray-300 shadow-sm transition hover:scale-110"
          style="background-color: #FFFFFF;"
          aria-label="White"
          on:click={() => {
            draftColor = "#FFFFFF";
            dispatch("update", { color: "#FFFFFF" });
          }}
        ></button>
      </div>
    </div>
  </Toolbar>
{/if}

<div
  use:tapout
  on:tapout={onBlur}
  class="absolute left-0 top-0 select-none"
  style="transform: translate({x + dx}px, {y + dy}px);"
>
  <div
    bind:this={editable}
    on:focus={onFocus}
    on:keydown={onKeydown}
    on:paste|preventDefault={onPaste}
    on:input={() =>
      dispatch("draft", { html: editable.innerHTML, lines: extractLines() })}
    contenteditable="true"
    spellcheck="false"
    class="outline-none whitespace-no-wrap relative z-10"
    style="font-size: {draftFontSize}px; font-family: '{draftFontFamily}', serif; line-height: {draftLineHeight}; color: {draftColor}; opacity: {draftOpacity}; -webkit-user-select: text; transform: scale({draftScaleX}, {draftScaleY}); transform-origin: left top;"
  />

  <div
    use:pannable
    on:panstart={handlePanStart}
    on:panmove={handlePanMove}
    on:panend={handlePanEnd}
    class="absolute w-full h-full left-0 top-0 rounded z-20"
    class:border={!hideBorders || selected || operation !== ""}
    class:border-dotted={!hideBorders || selected || operation !== ""}
    class:border-gray-400={!hideBorders || selected || operation !== ""}
    class:cursor-grab={!operation}
    class:cursor-grabbing={operation === "move"}
    class:editing={["edit", "tool"].includes(operation)}
  />
</div>

<style>
  .editing {
    @apply pointer-events-none border-gray-800 border-dashed;
  }
  .font-family {
    @apply block appearance-none h-8 w-full bg-white pl-3 pr-8 rounded-md leading-tight border border-gray-200;
  }
</style>
